import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// ============================================================
// GET APPROVAL SETTINGS
//
// GET /api/v1/house-approval-settings?pan_id=
// ============================================================

export const fetchHouseApprovalSettingsThunk =
  createAsyncThunk(
    "houseApprovalSettings/fetchSettings",

    async (
      panId,
      { rejectWithValue }
    ) => {
      try {
        const id =
          Number(panId);

        if (!id) {
          return rejectWithValue(
            "Please select one Panchayath"
          );
        }

        const res =
          await api.get(
            "/api/v1/house-approval-settings",
            {
              params: {
                pan_id: id,
              },
            }
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to load approval settings"
          );
        }

        return (
          res.data?.data ||
          null
        );
      } catch (error) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Failed to load approval settings"
        );
      }
    }
  );

// ============================================================
// GET APPROVER USERS
//
// GET
// /api/v1/house-approval-settings/users?pan_id=&search=
// ============================================================

export const fetchApprovalUsersThunk =
  createAsyncThunk(
    "houseApprovalSettings/fetchUsers",

    async (
      {
        pan_id,
        search = "",
      },
      { rejectWithValue }
    ) => {
      try {
        const id =
          Number(pan_id);

        if (!id) {
          return rejectWithValue(
            "Please select one Panchayath"
          );
        }

        const res =
          await api.get(
            "/api/v1/house-approval-settings/users",
            {
              params: {
                pan_id: id,
                search:
                  search || "",
              },
            }
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to load approval users"
          );
        }

        return (
          res.data?.data ||
          []
        );
      } catch (error) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Failed to load approval users"
        );
      }
    }
  );

// ============================================================
// SAVE APPROVAL SETTINGS
//
// PUT /api/v1/house-approval-settings
// ============================================================

export const saveHouseApprovalSettingsThunk =
  createAsyncThunk(
    "houseApprovalSettings/saveSettings",

    async (
      payload,
      { rejectWithValue }
    ) => {
      try {
        const panId =
          Number(
            payload?.pan_id
          );

        if (!panId) {
          return rejectWithValue(
            "Please select one Panchayath"
          );
        }

        const res =
          await api.put(
            "/api/v1/house-approval-settings",
            {
              pan_id:
                panId,

              disconnection_approval_required:
                Number(
                  payload
                    ?.disconnection_approval_required ||
                    0
                ),

              disconnection_approver_user_id:
                payload
                  ?.disconnection_approver_user_id ||
                null,

              reconnection_approval_required:
                Number(
                  payload
                    ?.reconnection_approval_required ||
                    0
                ),

              reconnection_approver_user_id:
                payload
                  ?.reconnection_approver_user_id ||
                null,

              discount_approval_required:
                Number(
                  payload
                    ?.discount_approval_required ||
                    0
                ),

              discount_approver_user_id:
                payload
                  ?.discount_approver_user_id ||
                null,
            }
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to save approval settings"
          );
        }

        return {
          data:
            res.data?.data ||
            null,

          message:
            res.data?.message ||
            "Approval settings saved successfully",
        };
      } catch (error) {
        return rejectWithValue(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Failed to save approval settings"
        );
      }
    }
  );

// ============================================================
// DEFAULT SETTINGS
// ============================================================

const defaultSettings = {
  pan_id: 0,

  disconnection_approval_required:
    0,

  disconnection_approver_user_id:
    null,

  reconnection_approval_required:
    0,

  reconnection_approver_user_id:
    null,

  discount_approval_required:
    0,

  discount_approver_user_id:
    null,
};

// ============================================================
// SLICE
// ============================================================

const houseApprovalSettingsSlice =
  createSlice({
    name:
      "houseApprovalSettings",

    initialState: {
      settings: {
        ...defaultSettings,
      },

      users: [],

      loading: false,

      usersLoading:
        false,

      saving: false,

      error: null,

      usersError:
        null,

      saveSuccess:
        false,

      saveMessage:
        "",
    },

    reducers: {
      clearHouseApprovalSettingsError:
        (state) => {
          state.error =
            null;

          state.usersError =
            null;
        },

      clearHouseApprovalSaveStatus:
        (state) => {
          state.saveSuccess =
            false;

          state.saveMessage =
            "";
        },

      resetHouseApprovalSettings:
        (state) => {
          state.settings = {
            ...defaultSettings,
          };

          state.users =
            [];

          state.loading =
            false;

          state.usersLoading =
            false;

          state.saving =
            false;

          state.error =
            null;

          state.usersError =
            null;

          state.saveSuccess =
            false;

          state.saveMessage =
            "";
        },
    },

    extraReducers:
      (builder) => {
        // ====================================================
        // FETCH SETTINGS
        // ====================================================

        builder
          .addCase(
            fetchHouseApprovalSettingsThunk.pending,
            (state) => {
              state.loading =
                true;

              state.error =
                null;
            }
          )

          .addCase(
            fetchHouseApprovalSettingsThunk.fulfilled,
            (
              state,
              action
            ) => {
              state.loading =
                false;

              state.settings = {
                ...defaultSettings,

                ...(action.payload ||
                  {}),
              };
            }
          )

          .addCase(
            fetchHouseApprovalSettingsThunk.rejected,
            (
              state,
              action
            ) => {
              state.loading =
                false;

              state.error =
                action.payload ||
                "Failed to load approval settings";
            }
          );

        // ====================================================
        // FETCH USERS
        // ====================================================

        builder
          .addCase(
            fetchApprovalUsersThunk.pending,
            (state) => {
              state.usersLoading =
                true;

              state.usersError =
                null;
            }
          )

          .addCase(
            fetchApprovalUsersThunk.fulfilled,
            (
              state,
              action
            ) => {
              state.usersLoading =
                false;

              state.users =
                Array.isArray(
                  action.payload
                )
                  ? action.payload
                  : [];
            }
          )

          .addCase(
            fetchApprovalUsersThunk.rejected,
            (
              state,
              action
            ) => {
              state.usersLoading =
                false;

              state.users =
                [];

              state.usersError =
                action.payload ||
                "Failed to load approval users";
            }
          );

        // ====================================================
        // SAVE
        // ====================================================

        builder
          .addCase(
            saveHouseApprovalSettingsThunk.pending,
            (state) => {
              state.saving =
                true;

              state.error =
                null;

              state.saveSuccess =
                false;

              state.saveMessage =
                "";
            }
          )

          .addCase(
            saveHouseApprovalSettingsThunk.fulfilled,
            (
              state,
              action
            ) => {
              state.saving =
                false;

              state.saveSuccess =
                true;

              state.saveMessage =
                action.payload
                  ?.message ||
                "Approval settings saved successfully";

              state.settings = {
                ...defaultSettings,

                ...(action.payload
                  ?.data ||
                  {}),
              };
            }
          )

          .addCase(
            saveHouseApprovalSettingsThunk.rejected,
            (
              state,
              action
            ) => {
              state.saving =
                false;

              state.saveSuccess =
                false;

              state.error =
                action.payload ||
                "Failed to save approval settings";
            }
          );
      },
  });

// ============================================================
// EXPORTS
// ============================================================

export const {
  clearHouseApprovalSettingsError,
  clearHouseApprovalSaveStatus,
  resetHouseApprovalSettings,
} =
  houseApprovalSettingsSlice.actions;

export default
houseApprovalSettingsSlice.reducer;